import { iconForName } from "@/components/artifacts/iconForName.ts";
import { useArtifactViewCtx } from "@/components/artifacts/view/ArtifactViewContext.tsx";
import { CollapsibleParticipants } from "@/components/artifacts/view/blocks/CollapsibleParticipants.tsx";
import { externalParticipantStyles } from "@/components/artifacts/view/blocks/participantStyles.ts";
import { Button } from "@/components/ui/button.tsx";
import { ContactInput } from "@/components/ui/contact-input.tsx";
import { ParticipantPill } from "@/components/ui/participant-pill.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { Toggle } from "@/components/ui/toggle.tsx";
import { contactQueries, createContactWithHandle } from "@/hooks/useContacts.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { cn } from "@/lib/utils.ts";
import type { ArtifactComponent, ViewParticipant } from "@/types/artifacts-view.ts";
import type { Contact, Handle } from "@/types/contact.ts";
import { useQuery } from "@tanstack/react-query";
import { Pencil, UsersRound } from "lucide-react";
import { useState } from "react";

type ParticipantListComponentData = Extract<ArtifactComponent, { type: "participantList" }>;

export const ParticipantListBlock = ({
  component,
}: {
  component: ParticipantListComponentData;
}) => {
  if (component.participantSource === "email")
    return <EmailParticipantList component={component} />;
  if (component.editMode === "inline") return <InlineParticipantList component={component} />;
  return <ModalParticipantList component={component} />;
};

// ---------------------------------------------------------------------------
/**
 * One trailing colon, whether or not the label arrived carrying one.
 *
 * A label that already ends in a colon would otherwise render as "From::".
 * Punctuation is presentation, so this row settles it rather than trusting every
 * view to write it the same way.
 *
 * Scoped to this row deliberately: the sibling lists in this file render their
 * labels raw or from a fixed map of their own, so normalising here changes only
 * the path that appends.
 */
const labelWithColon = (label: string) => `${label.replace(/:+$/, "")}:`;

// Email mode: label + email pills + ContactInput, always editable, no
// contact resolution. Used by the routine-config form for fromSenders /
// toRecipients / ccRecipients / calendar attendee filters / organizer.
// Pills carry `id === value === email`; write payload is `string[]` (the
// server applier maps that down to `string | undefined` for single fields).
// ---------------------------------------------------------------------------
const EmailParticipantList = ({ component }: { component: ParticipantListComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;
  const single = component.cardinality === "single";

  const writeEmails = async (next: string[]) => {
    if (!component.editPath) return;
    try {
      await patchOne(component.editPath, next);
    } catch (error) {
      console.error(error);
      errorToast("Error updating field");
    }
  };

  const handleAddEmail = async (email: string) => {
    if (component.participants.some((p) => p.value === email)) return;
    const next = single ? [email] : [...component.participants.map((p) => p.value), email];
    await writeEmails(next);
  };

  const handleRemove = async (email: string) => {
    const next = component.participants.filter((p) => p.value !== email).map((p) => p.value);
    await writeEmails(next);
  };

  const hideAddInput = single && component.participants.length > 0;

  return (
    <div className="flex items-start gap-2 mt-1.5">
      {component.label ? (
        <span className="text-xs text-muted-foreground w-28 shrink-0 pt-1">{component.label}</span>
      ) : null}
      <div className="flex flex-wrap items-center gap-1">
        {component.participants.map((participant) => (
          <ParticipantPill
            key={participant.value}
            participant={participant}
            variant="email"
            canDelete={canEdit}
            onDelete={() => handleRemove(participant.value)}
          />
        ))}
        {canEdit && !hideAddInput ? (
          <ContactInput
            onSelectContact={async (contact) => {
              const email = contact.handles?.find((h) => h.type === "emailAddress")?.value;
              if (email) await handleAddEmail(email);
            }}
            onSelectHandle={async (_contact, handle) => {
              if (handle.value) await handleAddEmail(handle.value);
            }}
            onSelectEmail={handleAddEmail}
            buttonClassName="h-6 w-6"
            statsigName="artifact-view-add-email"
          />
        ) : null}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Modal mode (invitation): pencil → reveal editable list with add/remove.
// ---------------------------------------------------------------------------

const ModalParticipantList = ({ component }: { component: ParticipantListComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;
  const [editing, setEditing] = useState(false);

  // An event with nobody on it is an ordinary event — a solo hold, or anything
  // booked onto a shared calendar. With no pills to show and no way to add any,
  // the row is a people glyph with blank space after it, so drop it entirely.
  // Keep it while it is editable: the pencil is the only way to invite someone.
  if (component.participants.length === 0 && !canEdit) return null;

  const writeAddressIds = async (next: ViewParticipant[]) => {
    if (!component.editPath) return;
    try {
      await patchOne(
        component.editPath,
        next.map((p) => p.id),
      );
    } catch (error) {
      console.error(error);
      errorToast("Error updating participants");
    }
  };

  const addParticipant = async (contact: Contact, selectedHandle?: Handle) => {
    const handle = selectedHandle ?? contact.handles?.find((h) => h.type === "emailAddress");
    if (!handle) {
      errorToast("Contact has no email address");
      return;
    }
    if (component.participants.some((p) => p.id === handle.id)) {
      errorToast(`${handle.value} is already a participant`);
      return;
    }
    const next: ViewParticipant[] = [
      ...component.participants,
      {
        id: handle.id,
        urn: handle.urn,
        name: contact.name,
        picture: contact.picture,
        value: handle.value,
        type: handle.type as "emailAddress" | "phoneNumber" | "address",
        contactId: contact.id,
        state: "unknown",
        isSelf: false,
        required: true,
      },
    ];
    await writeAddressIds(next);
  };

  const removeParticipant = async (participantId: string) => {
    const next = component.participants.filter((p) => p.id !== participantId);
    await writeAddressIds(next);
  };

  const body = editing ? (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row flex-wrap gap-1">
        {component.participants.map((participant) => (
          <ParticipantPill
            key={participant.id}
            participant={participant}
            variant="email"
            // The user's own entry is not removable here — taking yourself off
            // an event is `excludeSelf`, a durable choice the draft rule owns,
            // not a pill dismissal (BIG-6597).
            canDelete={!participant.isSelf}
            onDelete={() => removeParticipant(participant.id)}
          />
        ))}
        {/* `cardinality: "single"` hides the add affordance when a
         * recipient is already set. The user can still remove and
         * re-add to swap. */}
        {component.cardinality === "single" && component.participants.length > 0 ? null : (
          <ContactInput
            onSelectContact={addParticipant}
            onSelectHandle={(contact, handle) => addParticipant(contact, handle)}
            onSelectEmail={async (email) => {
              try {
                const contact = await createContactWithHandle({
                  contactName: "",
                  handleType: "emailAddress",
                  handleValue: email,
                });
                await addParticipant(contact);
              } catch (error) {
                console.error(error);
                errorToast("Error adding participant");
              }
            }}
            buttonClassName="h-6 w-6"
            statsigName="artifact-view-add-participant"
          />
        )}
      </div>
      <div className="flex flex-row gap-2">
        <Button
          onClick={() => setEditing(false)}
          variant="outlinePopover"
          size="action"
          className="h-6 text-xs px-2 py-1 font-medium"
          data-statsig-name="artifact-view-done-participants"
        >
          Done
        </Button>
      </div>
    </div>
  ) : (
    <div className="flex flex-row items-start gap-2">
      {/* `items-start` so each clickable pill (a center-justified Button)
          stays its natural width and left-aligns — otherwise the longer
          names center and the avatars don't line up. */}
      <CollapsibleParticipants
        participants={component.participants}
        renderParticipant={(participant) => (
          <ParticipantPill key={participant.id} participant={participant} />
        )}
        className="flex-col items-start"
      />
      {canEdit ? (
        component.participants.length === 0 ? (
          // A pencil next to an empty space reads as "edit what?". Name the
          // action instead, matching the event popover's empty guest list.
          <Button
            onClick={() => setEditing(true)}
            variant="outlinePopover"
            size="action"
            className="h-6 text-xs px-2 py-1 font-medium"
            data-statsig-name="artifact-view-add-participants"
          >
            Add participant
          </Button>
        ) : (
          <Button
            onClick={() => setEditing(true)}
            variant="ghost"
            size="action"
            className="h-6 w-6 p-0"
            aria-label="Edit participants"
            data-statsig-name="artifact-view-edit-participants"
          >
            <Pencil className="size-3" />
          </Button>
        )
      ) : null}
    </div>
  );

  // Role-based recipient lists (text/DM From/To, like the email draft): render
  // an inline "From:/To: [pill]" row with email pills and no RSVP status —
  // "optional" etc. only make sense for event attendees, not message senders.
  const roleLabel =
    component.role &&
    ({ from: "From:", to: "To:", cc: "Cc:", bcc: "Bcc:" } as const)[component.role];
  if (roleLabel) {
    return (
      <div className="flex flex-row items-start gap-2 text-sm">
        <div className="w-20 shrink-0 pt-0.5 font-medium">{roleLabel}</div>
        <CollapsibleParticipants
          participants={component.participants}
          renderParticipant={(participant) => (
            <ParticipantPill
              key={participant.id}
              participant={participant}
              variant="email"
              showAvatar
              showParticipantStatus={false}
            />
          )}
          className="flex-1"
        />
      </div>
    );
  }

  // Labeled lists (e.g. "Adding attendees" / "Removing attendees") render the
  // label as a header with its own icon, then the participants indented below —
  // so it's clear what the list is. Unlabeled lists keep the inline people glyph.
  if (component.label) {
    const HeaderIcon = component.icon ? iconForName(component.icon) : UsersRound;
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <HeaderIcon className="size-4" />
          <span>{component.label}</span>
        </div>
        <div className="pl-6">{body}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-row gap-2 items-start">
      <UsersRound className="size-5 mt-0.5" />
      <div className="flex flex-col gap-2 flex-1">{body}</div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline mode (kickoff): horizontal "To: Pill Pill +" with ContactInput
// inline. Each pill opens a popover with handle picker + Optional/Required
// toggle. Filters out the user's assistant (and the okthx assistant).
// ---------------------------------------------------------------------------

/** Map a ViewParticipant to the kickoff-specific patch shape. Optional
 * defaults to false when the source participant doesn't expose it. */
const toKickoffPatch = (p: ViewParticipant) => ({
  addressId: p.id,
  contactId: p.contactId ?? "",
  optional: p.optional ?? false,
});

const InlineParticipantList = ({ component }: { component: ParticipantListComponentData }) => {
  const { isReadOnly, patchOne } = useArtifactViewCtx();
  const canEdit = !isReadOnly && component.editable;
  const { userContext } = useCurrentUserContext();

  const userAssistantEmail = userContext?.user.assistantUserAlias;
  const isAssistant = (p: ViewParticipant) =>
    p.value === "okthx@okthx.com" || p.value === userAssistantEmail;

  // Hiding the assistant is specific to the editable kickoff attendee picker,
  // where okthx is implicitly always present. Role-based lists (inbound email
  // From/To/Cc) must show exactly who was on the message — including okthx when
  // the user CC'd it (BIG-5060).
  const visible = component.role
    ? component.participants
    : component.participants.filter((p) => !isAssistant(p));

  const writeKickoffParticipants = async (
    next: Array<{ addressId: string; contactId: string; optional: boolean }>,
  ) => {
    if (!component.editPath) return;
    try {
      await patchOne(component.editPath, next);
    } catch (error) {
      console.error(error);
      errorToast("Error updating participants");
    }
  };

  const handleToggleOptional = (participantId: string) => {
    const next = visible.map((p) =>
      p.id === participantId
        ? { ...toKickoffPatch(p), optional: !(p.optional ?? false) }
        : toKickoffPatch(p),
    );
    void writeKickoffParticipants(next);
  };

  const handleChangeHandle = (participantId: string, newHandleId: string) => {
    const next = visible.map((p) =>
      p.id === participantId ? { ...toKickoffPatch(p), addressId: newHandleId } : toKickoffPatch(p),
    );
    void writeKickoffParticipants(next);
  };

  const handleRemove = (participantId: string) => {
    const next = visible.filter((p) => p.id !== participantId).map(toKickoffPatch);
    void writeKickoffParticipants(next);
  };

  const handleAdd = (contact: Contact, handle: Handle) => {
    if (visible.some((p) => p.id === handle.id)) return;
    const next = [
      ...visible.map(toKickoffPatch),
      { addressId: handle.id, contactId: contact.id, optional: false },
    ];
    void writeKickoffParticipants(next);
  };

  return (
    <div className="flex items-start gap-2 text-sm">
      <div
        className={cn(
          "shrink-0",
          // Read-only lists (e.g. inbound communications) use the compact muted
          // label; editable lists (scheduling kickoff) keep the prominent label.
          canEdit ? "font-medium" : "w-12 pt-0.5 text-xs text-muted-foreground",
        )}
      >
        {labelWithColon(component.label || "To")}
      </div>
      <div className="flex flex-wrap gap-1.5 items-center flex-1 min-w-0">
        {visible.map((participant) => (
          <KickoffParticipantPill
            key={participant.id}
            participant={participant}
            canEdit={canEdit}
            allowChangeHandle={!!component.allowChangeHandle}
            allowToggleOptional={!!component.allowToggleOptional}
            userAssistantEmail={userAssistantEmail}
            onChangeHandle={(handleId) => handleChangeHandle(participant.id, handleId)}
            onToggleOptional={() => handleToggleOptional(participant.id)}
            onDelete={() => handleRemove(participant.id)}
          />
        ))}
        {canEdit ? (
          <ContactInput
            onSelectContact={() => {}}
            onSelectHandle={handleAdd}
            onSelectEmail={async (email) => {
              const contact = await createContactWithHandle({
                contactName: "",
                handleType: "emailAddress",
                handleValue: email,
              });
              const handle = contact.handles?.[0];
              if (handle) handleAdd(contact, handle);
            }}
            filterHandleTypes={["emailAddress"]}
            statsigName="artifact-view-inline-add-participant"
          />
        ) : null}
      </div>
    </div>
  );
};

const KickoffParticipantPill = ({
  participant,
  canEdit,
  allowChangeHandle,
  allowToggleOptional,
  userAssistantEmail,
  onChangeHandle,
  onToggleOptional,
  onDelete,
}: {
  participant: ViewParticipant;
  canEdit: boolean;
  allowChangeHandle: boolean;
  allowToggleOptional: boolean;
  userAssistantEmail: string | undefined;
  onChangeHandle: (handleId: string) => void;
  onToggleOptional: () => void;
  onDelete: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const isOwnAssistant = participant.value === userAssistantEmail;
  const isAssistant = participant.value === "okthx@okthx.com";
  const isOptional = participant.optional ?? false;

  const pill = (
    <ParticipantPill
      participant={{ ...participant, required: !isOptional }}
      variant="email"
      showAvatar={true}
      showParticipantStatus={false}
      isExternal={!participant.isDomainHandle}
      className={cn(
        !participant.isDomainHandle && externalParticipantStyles,
        "w-fit",
        canEdit && "cursor-pointer",
      )}
      isAssistant={isAssistant}
      isOwnAssistant={isOwnAssistant}
      canDelete={canEdit}
      onDelete={onDelete}
    />
  );

  if (!canEdit || (!allowChangeHandle && !allowToggleOptional)) return pill;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="blank" size="fit" data-statsig-name="artifact-participant-edit">
          {pill}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-0">
        <KickoffParticipantPopover
          participant={participant}
          isOptional={isOptional}
          allowChangeHandle={allowChangeHandle}
          allowToggleOptional={allowToggleOptional}
          onChangeHandle={(handleId) => {
            onChangeHandle(handleId);
            setOpen(false);
          }}
          onToggleOptional={onToggleOptional}
        />
      </PopoverContent>
    </Popover>
  );
};

const KickoffParticipantPopover = ({
  participant,
  isOptional,
  allowChangeHandle,
  allowToggleOptional,
  onChangeHandle,
  onToggleOptional,
}: {
  participant: ViewParticipant;
  isOptional: boolean;
  allowChangeHandle: boolean;
  allowToggleOptional: boolean;
  onChangeHandle: (handleId: string) => void;
  onToggleOptional: () => void;
}) => {
  const { data: contact } = useQuery(contactQueries.detail(participant.contactId));
  const emailHandles = contact?.handles?.filter((h) => h.type === "emailAddress") ?? [];

  return (
    <div className="flex flex-col gap-3 p-3">
      {allowChangeHandle && emailHandles.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <div className="text-xs font-medium text-muted-foreground">Send to</div>
          {emailHandles.map((handle) => (
            <label
              key={handle.id}
              className={cn(
                "flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm cursor-pointer hover:bg-muted/50",
                handle.id === participant.id && "bg-muted",
              )}
            >
              <span className={handle.id === participant.id ? "font-medium" : ""}>
                {handle.value}
              </span>
              <input
                type="radio"
                name="handle"
                checked={handle.id === participant.id}
                onChange={() => onChangeHandle(handle.id)}
                className="accent-primary shrink-0"
              />
            </label>
          ))}
        </div>
      ) : null}
      {allowToggleOptional ? (
        <div className="flex flex-col gap-1.5">
          <div className="text-xs font-medium text-muted-foreground">Attendance</div>
          <Toggle
            checked={!isOptional}
            onCheckedChange={(checked) => {
              if (checked !== !isOptional) onToggleOptional();
            }}
            leftLabel="Optional"
            rightLabel="Required"
          />
        </div>
      ) : null}
    </div>
  );
};
